import { inject, Service } from '@angular/core';
import { CommercialFilter } from '@adgate/modules/tv/types/commercial-filter.type';
import { NetworkService } from '@adgate/common/services/network-service';
import { Commercial } from '@adgate/modules/tv/types/commecial.type';
import { DialogService } from '@adgate/common/services/dialog-service';
import { finalize, switchMap, tap, last, map, throwError } from 'rxjs';
import { environment } from '@env/environment';
import { RESTApplicationResponse } from '@adgate/common/types/rest-application-response.type';
import { AsperaService } from '@adgate/common/services/aspera-service';
import { BaseService } from '@adgate/common/services/base-service';
import { StringUtils } from '@adgate/common/components/helpers/string-utils.helper';


@Service()
export class TvService extends BaseService
{
    private asperaService:AsperaService = inject(AsperaService);
    private dialogService:DialogService = inject(DialogService);
    
    private baseServiceUrl = `${environment.baseServiceUrl}tv`;

    getCommercials(filter: CommercialFilter | null)
    {
        let url = `${this.baseServiceUrl}/getCommercials`;
        return this.sendRequest(url, filter, 'post');
    }

    createCommercial(commercial: Commercial)
    {
        let url = `${this.baseServiceUrl}/createCommercial`;
        return this.sendRequest(url, commercial, 'post');
    }

    createAndUploadCommercial_HTTP(commercial: Commercial, mediaFile: File)
    {
        let dh: any;
        let url = `${this.baseServiceUrl}/createCommercial`;
        let file = { data: mediaFile, state: 'in', inProgress: false, progress: 0, uploadedSize: 0, canRetry: false, canCancel: true };

        dh = this.dialogService.openUploadProgressDialog( file );

        return this.getNetworkService().uploadFile(url, file, commercial).pipe(
            switchMap((r:RESTApplicationResponse) => {
                return this.testError(r);
            }),
            finalize(() => dh.close())
        );
    }

    uploadCommercial_HTTP(commercialId: number, referenceCode: string, mediaFile: File)
    {
        let dh: any;
        let url = `${this.baseServiceUrl}/uploadCommercial/${commercialId}?refCode=${referenceCode}`;
        let file = { data: mediaFile, state: 'in', inProgress: false, progress: 0, uploadedSize: 0, canRetry: false, canCancel: true };

        dh = this.dialogService.openUploadProgressDialog( file );

        return this.getNetworkService().uploadFile(url, file, null).pipe(
            switchMap((r:RESTApplicationResponse) => {
                return this.testError(r);
            }),
            finalize(() => dh.close())
        );
    }

    createAndUploadCommercial_ASPERA(commercial: Commercial, dataTransfer: any)
    {
        let filename = StringUtils.filterFilename(dataTransfer.files[0].name);
        let file = { data: {name: filename, size: dataTransfer.files[0].size} as File, state: 'in', inProgress: false, progress: 0, uploadedSize: 0, canRetry: false, canCancel: true };
        let dh: any;

        return this.createCommercial(commercial).pipe(
            switchMap((r: RESTApplicationResponse) => {
                const newCommercial: Commercial = r.data;

                this.dialogService.hideProgress();
                dh = this.dialogService.openUploadProgressDialog(file);

                return this.asperaService.upload(dataTransfer).pipe(
                    tap(progress => {
                        file.progress = progress.percentage;
                        file.uploadedSize = progress.bytesTransferred || 0;
                    }),
                    last(),
                    map(finalProgress => ({
                        success: finalProgress.status === 'completed',
                        message: finalProgress.status === 'completed'
                            ? 'Upload completed successfully'
                            : `Upload ${finalProgress.status}`,
                        data: newCommercial
                    })),
                    switchMap((uploadResult: RESTApplicationResponse) => {
                        if (!uploadResult.success) {
                            return throwError(() => new Error(uploadResult.message));
                        } else {
                            return this.notifyAsperaUpload(newCommercial.id, filename, newCommercial.referenceCode);
                        }
                    })
                );
            }),
            finalize(() => dh.close())
        );
    }

    uploadCommercial_ASPERA(commercialId: number, referenceCode: string, dataTransfer: any)
    {
        let filename = StringUtils.filterFilename(dataTransfer.files[0].name);
        let file = { data: {name: filename, size: dataTransfer.files[0].size} as File, state: 'in', inProgress: false, progress: 0, uploadedSize: 0, canRetry: false, canCancel: true };
        let dh: any;

        this.dialogService.hideProgress();
        dh = this.dialogService.openUploadProgressDialog(file);

        return this.asperaService.upload(dataTransfer).pipe(
            tap(progress => {
                file.progress = progress.percentage;
                file.uploadedSize = progress.bytesTransferred || 0;
            }),
            last(),
            map(finalProgress => ({
                success: finalProgress.status === 'completed',
                message: finalProgress.status === 'completed'
                    ? 'Upload completed successfully'
                    : `Upload ${finalProgress.status}`,
                data: null
            })),
            switchMap((r: RESTApplicationResponse) => {
                if (!r.success) {
                    return throwError(() => new Error(r.message));
                } else {
                    return this.notifyAsperaUpload(commercialId, filename, referenceCode);
                }
            }),
            finalize(() => dh.close())
        );
    }

    notifyAsperaUpload(commercialId: number, fileName: string, referenceCode: string | null)
    {
        let url = `${this.baseServiceUrl}/notifyAsperaUpload/${commercialId}`;
        let data = { fileName: fileName, referenceCode: referenceCode }

        return this.sendRequest(url, data, 'post');
    }

    deleteCommercial(commercialId: number)
    {
        let url = `${this.baseServiceUrl}/deleteCommercial/${commercialId}`;
        return this.sendRequest(url, null, 'delete');
    }
}

